import { useState } from 'react';
import type { ProjectFile } from '@/types/video-enhancer';
import { CreditOperation } from '@/hooks/useCredits';

// Global singleton to track completed jobs across all instances
const globalCompletedJobs = new Set<string>();

interface UseBackgroundRemovalParams {
  files: ProjectFile[];
  setFiles: React.Dispatch<React.SetStateAction<ProjectFile[]>>;
  toast: (opts: { title: string; description?: string; variant?: string }) => void;
  requireCreditConfirmation: (
    operationType: CreditOperation,
    operationName: string,
    onConfirm: () => void
  ) => void;
  CREDIT_OPERATIONS: { BACKGROUND_REMOVAL: CreditOperation };
}

export function useBackgroundRemoval({
  files,
  setFiles,
  toast,
  requireCreditConfirmation,
  CREDIT_OPERATIONS,
}: UseBackgroundRemovalParams) {
  const [showRemoveBgModal, setShowRemoveBgModal] = useState(false);
  const [pendingRemoveBgFiles, setPendingRemoveBgFiles] = useState<string[]>([]);
  const [selectedOutputType, setSelectedOutputType] = useState('foreground-mask');
  const [showBackgroundSelector, setShowBackgroundSelector] = useState(false);
  const [selectedOriginalFile, setSelectedOriginalFile] = useState<ProjectFile | null>(null);
  const [selectedAlphaMask, setSelectedAlphaMask] = useState<ProjectFile | null>(null);
  const [selectedBackground, setSelectedBackground] = useState<string>('');
  const [activePollJobs, setActivePollJobs] = useState<Set<string>>(new Set());
  const [completedJobs, setCompletedJobs] = useState<Set<string>>(new Set());

  const handleRemoveBackground = async (fileIds: string[], outputType: string = 'green-screen') => {
    const selectedFilesToProcess = files.filter(f => fileIds.includes(f.id) && f.fileType === 'video');

    if (selectedFilesToProcess.length === 0) {
      toast({
        title: "No video files selected",
        description: "Please select video files to process",
        variant: "destructive"
      });
      return;
    }

    setPendingRemoveBgFiles(fileIds);
    setShowRemoveBgModal(true);
  };

  const executeRemoveBackground = async (outputType: string) => {
    const selectedFilesToProcess = files.filter(f => pendingRemoveBgFiles.includes(f.id) && f.fileType === 'video');
    const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');

    for (const file of selectedFilesToProcess) {
      try {
        const outputDescription = outputType === 'green-screen' ? 'green screen version' :
                                  outputType === 'foreground-mask' ? 'luma matte for Shotstack' : 'transparency mask';
        toast({
          title: "Removing background",
          description: `Creating ${outputDescription} for ${file.name}...`,
        });

        const response = await fetch('/api/remove-video-background', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({
            videoUrl: file.s3Url,
            videoKey: file.s3Key,
            outputType: outputType
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.details || error.error || 'Alpha mask generation failed');
        }

        const result = await response.json();
        console.log('Background removal API response:', result);

        if (result.jobId) {
          toast({
            title: "Background removal started",
            description: `Generating ${outputDescription} for ${file.name}. This may take a few minutes...`,
          });

          const processingJobId = `${result.jobId}:${outputType}`;

          setFiles(prev => prev.map(f =>
            f.id === file.id
              ? { ...f, processingStatus: 'starting' as const, processingJobId: processingJobId }
              : f
          ));

          pollAlphaMaskGeneration(result.jobId, file);
        }
      } catch (error: any) {
        console.error('Background removal error:', error);
        toast({
          title: "Background removal failed",
          description: error.message || 'Failed to start background removal',
          variant: "destructive"
        });
      }
    }
  };

  const processRemoveBackground = (outputType: string) => {
    requireCreditConfirmation(
      CREDIT_OPERATIONS.BACKGROUND_REMOVAL,
      'Background Removal',
      () => executeRemoveBackground(outputType)
    );
  };

  const pollAlphaMaskGeneration = async (jobId: string, originalFile: ProjectFile) => {
    if (activePollJobs.has(jobId)) {
      return;
    }

    setActivePollJobs(prev => new Set([...prev, jobId]));

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`/api/remove-video-background/status/${jobId}`);
        const result = await response.json();

        if (result.status === 'completed') {
          clearInterval(pollInterval);
          setActivePollJobs(prev => {
            const newSet = new Set(prev);
            newSet.delete(jobId);
            return newSet;
          });

          if (globalCompletedJobs.has(jobId)) {
            return;
          }

          globalCompletedJobs.add(jobId);
          setCompletedJobs(prev => new Set([...prev, jobId]));

          setFiles(prev => {
            const origFile = prev.find(f => f.processingJobId && f.processingJobId.startsWith(jobId));
            if (!origFile) {
              console.error('Could not find original file for job:', jobId);
              return prev;
            }

            const jobIdWithType = origFile.processingJobId;
            const isGreenScreen = jobIdWithType && jobIdWithType.includes('green-screen');
            const isForegroundMask = jobIdWithType && jobIdWithType.includes('foreground-mask');
            const outputSuffix = isGreenScreen ? 'Green Screen' :
                                 isForegroundMask ? 'Luma Matte' : 'Alpha Mask';

            if (isForegroundMask) {
              const pairId = `pair_${Date.now()}`;
              const maskFileId = `${origFile.id}_mask`;

              const updatedFiles = prev.map(f => {
                if (f.id === origFile.id) {
                  return {
                    ...f,
                    processingStatus: undefined,
                    processingJobId: undefined,
                    linkedPairId: pairId,
                    isLinked: true,
                    linkedMaskId: maskFileId,
                    displayName: `${f.name} [Original + Audio]`,
                    layerType: 'main' as const
                  };
                }
                return f;
              });

              const maskVideoUrl = result.videoUrl.startsWith('/api/s3/')
                ? `${window.location.origin}${result.videoUrl}`
                : result.videoUrl;

              const maskFile: ProjectFile = {
                id: maskFileId,
                name: `${origFile.name}_mask`,
                displayName: `${origFile.name} [Luma Matte]`,
                fileType: 'video',
                thumbnailUrl: origFile.thumbnailUrl,
                duration: origFile.duration,
                s3Url: maskVideoUrl,
                s3Key: result.s3Key,
                linkedPairId: pairId,
                isLinked: true,
                linkedVideoId: origFile.id,
                layerType: 'mask',
                imported: origFile.imported
              };

              return [...updatedFiles, maskFile];
            } else {
              const updatedFiles = prev.map(f => {
                if (f.id === origFile.id) {
                  return {
                    ...f,
                    processingStatus: undefined,
                    processingJobId: undefined,
                    hasGreenScreen: true,
                    greenScreenUrl: result.videoUrl,
                    greenScreenKey: result.s3Key,
                    greenScreenType: (isGreenScreen ? 'green-screen' : 'alpha-mask') as 'green-screen' | 'alpha-mask',
                    hasAudio: result.hasAudio !== false,
                    enhancementPrompt: isGreenScreen ? `chromaKey #97eca9` : '',
                    displayName: `${f.name} [${outputSuffix} ✓]`
                  };
                }
                return f;
              });

              return updatedFiles;
            }
          });

          const jobIdWithType = originalFile.processingJobId;
          const isForegroundMask = jobIdWithType && jobIdWithType.includes('foreground-mask');
          const isGreenScreen = jobIdWithType && jobIdWithType.includes('green-screen');
          const outputSuffix = isGreenScreen ? 'Green Screen' :
                               isForegroundMask ? 'Luma Matte' : 'Alpha Mask';

          toast({
            title: "Background removal complete!",
            description: `${outputSuffix} version is now available for ${originalFile.displayName || originalFile.name} with audio preserved.`
          });
        } else if (result.status === 'failed' || result.status === 'expired') {
          clearInterval(pollInterval);
          setActivePollJobs(prev => {
            const newSet = new Set(prev);
            newSet.delete(jobId);
            return newSet;
          });

          setFiles(prev => prev.map(f =>
            f.processingJobId && f.processingJobId.startsWith(jobId)
              ? { ...f, processingStatus: 'failed' as const, processingJobId: undefined }
              : f
          ));

          if (result.status === 'expired') {
            toast({
              title: "Background removal expired",
              description: "This job took too long and expired. Replicate removes output files after 1 hour. Please try again.",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Alpha mask generation failed",
              description: result.error || 'Processing failed',
              variant: "destructive"
            });
          }
        } else {
          const newStatus = result.status === 'starting' ? 'starting' : result.status === 'processing' ? 'processing' : 'starting';
          setFiles(prev => prev.map(f =>
            f.processingJobId && f.processingJobId.startsWith(jobId)
              ? { ...f, processingStatus: newStatus as any }
              : f
          ));
        }
      } catch (error) {
        console.error('Frontend polling error:', error);
      }
    }, 5000);

    setTimeout(() => {
      clearInterval(pollInterval);
      setActivePollJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
      setFiles(prev => prev.map(f =>
        f.processingJobId && f.processingJobId.startsWith(jobId)
          ? { ...f, processingStatus: 'failed' as const, processingJobId: undefined }
          : f
      ));
      toast({
        title: "Background removal timed out",
        description: "The operation took longer than expected and has been cancelled",
        variant: "destructive"
      });
    }, 3600000);
  };

  const openBackgroundSelector = (originalFile: ProjectFile, alphaMaskFile: ProjectFile) => {
    setSelectedOriginalFile(originalFile);
    setSelectedAlphaMask(alphaMaskFile);
    setSelectedBackground('');
    setShowBackgroundSelector(true);
  };

  const handleCompositeBackground = async () => {
    if (!selectedOriginalFile || !selectedAlphaMask || !selectedBackground) {
      toast({
        title: "Missing requirements",
        description: "Please select a background before compositing",
        variant: "destructive"
      });
      return;
    }

    try {
      setShowBackgroundSelector(false);

      toast({
        title: "Starting composition",
        description: `Compositing ${selectedOriginalFile.name} with selected background...`,
      });

      const response = await fetch('/api/composite-background', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          originalVideoUrl: selectedOriginalFile.s3Url,
          alphaMaskUrl: selectedAlphaMask.s3Url,
          backgroundUrl: selectedBackground,
          outputName: `${selectedOriginalFile.name} (Composite)`
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Composition failed');
      }

      const result = await response.json();

      if (result.renderId) {
        toast({
          title: "Composition started",
          description: `Compositing ${selectedOriginalFile.name}. This may take a few minutes...`,
        });

        pollCompositionStatus(result.renderId, selectedOriginalFile, selectedAlphaMask);
      }
    } catch (error: any) {
      console.error('Composition error:', error);
      toast({
        title: "Composition failed",
        description: error.message || 'Failed to start composition',
        variant: "destructive"
      });
    }
  };

  const pollCompositionStatus = async (renderId: string, originalFile: ProjectFile, alphaMask: ProjectFile) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`/api/composite-background/status/${renderId}`);
        const result = await response.json();

        if (result.status === 'completed' && result.videoUrl) {
          clearInterval(pollInterval);

          const compositedFile: ProjectFile = {
            id: Math.random().toString(36).substr(2, 9),
            name: `${originalFile.name} (Composite)`,
            fileType: 'video',
            thumbnailUrl: originalFile.thumbnailUrl,
            duration: originalFile.duration,
            timestamp: new Date().toISOString(),
            s3Url: result.videoUrl,
            imported: false,
            enhanced: true,
            enhancementPrompt: `Composited with Shotstack - background + alpha mask`,
          };

          setFiles(prev => [...prev, compositedFile]);

          toast({
            title: "Composition complete!",
            description: `${originalFile.name} has been composited successfully with audio preserved`,
          });
        } else if (result.status === 'failed') {
          clearInterval(pollInterval);

          toast({
            title: "Composition failed",
            description: result.error || 'Processing failed',
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Composition polling error:', error);
      }
    }, 5000);

    setTimeout(() => clearInterval(pollInterval), 900000);
  };

  return {
    showRemoveBgModal,
    setShowRemoveBgModal,
    pendingRemoveBgFiles,
    selectedOutputType,
    setSelectedOutputType,
    showBackgroundSelector,
    setShowBackgroundSelector,
    selectedOriginalFile,
    selectedAlphaMask,
    selectedBackground,
    setSelectedBackground,
    activePollJobs,
    completedJobs,
    handleRemoveBackground,
    executeRemoveBackground,
    processRemoveBackground,
    openBackgroundSelector,
    handleCompositeBackground,
  };
}
